<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-mailbox" class="q-mb-sm" /> {{ $t('mailing_list') }}
		</div>
		<div>
			<q-table
				ref="table"
				:data="users"
				:columns="columns"
				row-key="id"
				:pagination.sync="server_pagination"
				@request="requestUsers"
				:loading="loading"
				:filter="filter"
				selection="multiple"
				:no-data-label="$t('no_data_avaliable')"
				:no-results-label="$t('no_results_found')"
				:loading-label="$t('loading_data')"
				:rows-per-page-label="$t('items_per_page')"
				:rows-per-page-options="[5, 10, 50, 100]"
				binary-state-sort
				:selected.sync="selected_users"
				class="q-mt-md"
			>
				<template slot="top" slot-scope="props">
					<q-search
						v-model="filter"
						:debounce="600"
						icon="far fa-search"
						:placeholder="$t('search')"
					/>
					<div class="col" />
					<q-btn
						color="secondary"
						v-if="reportsPermission"
						icon="fas fa-download"
						class="q-mr-sm"
						:disable="$buttonDelay('mailing_report_button_delay')"
						:label="$t('mailing_reports')"
						@click="downloadReport()"
					>
						<q-tooltip
							v-if="$buttonDelay('mailing_report_button_delay')"
							anchor="top middle"
							self="bottom middle"
							:offset="[10, 10]"
						>
							{{ $t('report_button_delay') }}
						</q-tooltip>
					</q-btn>
					<q-btn
						color="negative"
						icon="far fa-ban"
						:label="$t('remove_subscriptions')"
						@click="unsubscribeUsers"
						class="q-mr-sm"
						:disable="!selected_users.length"
						><q-tooltip>
							{{
								selected_users.length
									? $t('subscribe_users_tooltip')
									: $t('select_users_tooltip')
							}}
						</q-tooltip>
					</q-btn>
					<q-btn
						color="secondary"
						icon="far fa-plus"
						:label="$t('subscribe')"
						@click="subscribeUsers"
						:disable="!selected_users.length"
						><q-tooltip>
							{{
								selected_users.length
									? $t('unsubscribe_users_tooltip')
									: $t('select_users_tooltip')
							}}
						</q-tooltip>
					</q-btn>
				</template>

				<q-tr slot="header" slot-scope="props" :props="props">
					<q-th auto-width>
						<q-checkbox
							v-model="props.selected"
							indeterminate-value="some"
							:disable="loading"
						/>
					</q-th>

					<q-th v-for="col in props.cols" :key="col.name" :props="props">
						{{ $t(col.label) }}
					</q-th>
				</q-tr>

				<q-tr slot="body" slot-scope="props">
					<q-td auto-width>
						<q-checkbox color="primary" v-model="props.selected" :disable="loading" />
					</q-td>

					<q-td key="id" :props="props">
						{{ props.row.id }}
					</q-td>

					<q-td key="name" :props="props">
						{{ props.row.full_name }}
					</q-td>

					<q-td key="email" :props="props">
						{{ props.row.email }}
					</q-td>

					<q-td key="email_unsubscription_date" :props="props">
						{{ props.row.email_unsubscription_date }}
					</q-td>

					<q-td auto-width key="email_subscription" :props="props">
						<q-btn-toggle
							v-model="props.row.email_subscription"
							:toggle-color="props.row.email_subscription ? 'positive' : 'negative'"
							:options="statuses"
							@click.native="changeSingleUserSubscription((user = props.row))"
						/>
					</q-td>
				</q-tr>
			</q-table>
		</div>
		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3 order-last">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_overview' })"
				/>
			</div>
		</div>
	</q-page>
</template>

<script>
import { date } from 'quasar'
const { formatDate } = date
import { mapGetters } from 'vuex'

export default {
	name: 'AdminMailingListConfig',

	i18nOptions: { namespaces: ['common', 'admin'] },

	data() {
		return {
			loading: false,
			users: [],
			selected_users: [],
			filter: '',
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
				},
				{
					name: 'name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'full_name',
					sortable: false,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: false,
				},
				{
					name: 'email_unsubscription_date',
					required: true,
					label: 'cancelation_date',
					align: 'left',
					field: 'email_unsubscription_date',
					sortable: false,
				},
				{
					name: 'email_subscription',
					required: true,
					label: 'subscribed',
					align: 'center',
					field: 'email_subscription',
					sortable: false,
				},
			],
			statuses: [
				{ label: this.$t('yes'), value: true },
				{ label: this.$t('no'), value: false },
			],
		}
	},

	components: {},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			num_rows: 'datatable/num_rows',
			user_client_module_items: 'common/user_client_module_items',
			id_user_client_selected: 'common/id_user_client_selected',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		reportsPermission() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'reports'
				})
				if (item) {
					return item.permission
				}
			}
			return false
		},
	},

	methods: {
		async requestUsers({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page: this.server_pagination.page,
				page_size: this.server_pagination.rowsPerPage,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			try {
				const response = await this.$axios.get(
					`/admin_users/users_mailing/?${this.$qs.stringify(data, {
						arrayFormat: 'repeat',
					})}`,
					this.getHeader
				)

				this.server_pagination.rowsNumber = response.data.count
				this.users = response.data.results.map((u) => ({
					...u,
					email_unsubscription_date:
						u.email_unsubscription_date &&
						date.formatDate(u.email_unsubscription_date, 'DD/MM/YYYY HH:mm'),
				}))
			} catch (error) {
				console.log(error)
				this.$q.notify({
					message: this.$t('error_loading_users_list'),
					type: 'negative',
					icon: 'fal fa-ban',
				})
			} finally {
				this.loading = false
			}
		},

		async subscribeUsers(user = null) {
			this.loading = true
			const userCheck = user && !isNaN(user)
			const data = {}
			userCheck ? (data.users = [user]) : (data.users = this.selected_users.map((u) => u.id))

			try {
				const response = await this.$axios.post(
					'/admin_users/users_mailing/user_subscribe_mailing/',
					data,
					this.getHeader
				)
				if (userCheck) {
					const matchUser = this.users.find((u) => u.id === user)
					if (matchUser)
						this.users = this.users.map((user) =>
							user.id !== matchUser.id
								? user
								: {
										...matchUser,
										email_subscription: true,
										email_unsubscription_date: null,
								  }
						)
				} else {
					this.users = this.users.map((u) => ({
						...u,
						email_subscription: true,
						email_unsubscription_date: null,
					}))
				}

				this.$q.notify({
					message: this.$t('save_data_success'),
					type: 'positive',
					icon: 'fal fa-save',
				})
			} catch (error) {
				console.log(error)
				this.$q.notify({
					message: this.$t('save_data_error'),
					type: 'negative',
					icon: 'fal fa-ban',
				})
			} finally {
				this.loading = false
			}
		},

		async unsubscribeUsers(user = null) {
			this.loading = true
			const userCheck = user && !isNaN(user)
			const data = {}
			userCheck ? (data.users = [user]) : (data.users = this.selected_users.map((u) => u.id))

			try {
				const response = await this.$axios.post(
					'/admin_users/users_mailing/user_unsubscribe_mailing/',
					data,
					this.getHeader
				)
				const dateNow = new Date()
				if (userCheck) {
					const matchUser = this.users.find((u) => u.id === user)
					if (matchUser)
						this.users = this.users.map((user) =>
							user.id !== matchUser.id
								? user
								: {
										...matchUser,
										email_subscription: false,
										email_unsubscription_date: date.formatDate(
											dateNow,
											'DD/MM/YYYY HH:mm'
										),
								  }
						)
				} else {
					this.users = this.users.map((u) => ({
						...u,
						email_subscription: false,
						email_unsubscription_date: date.formatDate(dateNow, 'DD/MM/YYYY HH:mm'),
					}))
				}

				this.$q.notify({
					message: this.$t('save_data_success'),
					type: 'positive',
					icon: 'fal fa-save',
				})
			} catch (error) {
				console.log(error)
				this.$q.notify({
					message: this.$t('save_data_error'),
					type: 'negative',
					icon: 'fal fa-ban',
				})
			} finally {
				this.loading = false
			}
		},

		changeSingleUserSubscription(user) {
			user.email_subscription ? this.subscribeUsers(user.id) : this.unsubscribeUsers(user.id)
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		downloadReport() {
			if (this.$buttonDelay('mailing_report_button_delay')) return
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})

			this.$axios
				.get(`admin_users/users_mailing/generate_report/`, this.getHeader)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$store.dispatch('common/SET_MAILING_REPORT_BUTTON_DELAY', [new Date()])
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},
	},

	created() {
		this.loadPagination()
		this.requestUsers({ pagination: this.server_pagination })
	},
}
</script>

<style lang="stylus" scoped></style>
